import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import {
    GifPreview,
    Kudos,
    LinkPreview,
    Reactions,
    ReactionsComponent,
    Threads,
    TimelineItem,
    TimelineItemType
} from "@teammood/timeline";
import { TranslateModule } from "@ngx-translate/core";
import { AsyncPipe, NgClass } from "@angular/common";
import { GifPreviewComponent } from "../../../shared/ui/gifpreview/gifpreview";
import { EditKudosBoxComponent } from "./edit-kudos-box/edit-kudos-box.component";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { distinctUntilChanged, map } from "rxjs";
import { MatTooltip } from "@angular/material/tooltip";
import { ThreadsComponent } from "../../threads/threads.component";
import { KudosRecipientsDisplayComponent } from "./kudos-recipients-display.component";
import { LinkPreviewComponent } from "../../../shared/ui/linkpreview/linkpreview";
import { LinkyModule } from "@teammood/shared/util/linky";
import { MatMenuModule } from "@angular/material/menu";
import { UserInfos } from "../../../shared/ui/username-icon/user-infos.type";
import { ReviewBadgeComponent } from "../review-badge/review-badge.component";
import { PrivateBadgeComponent } from "../private-badge/private-badge.component";
import { ManagerPanelComponent } from "../manager-panel/manager-panel.component";

@Component({
    selector: "tm-kudos-card",
    templateUrl: "./kudos-card.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateModule,
        NgClass,
        GifPreviewComponent,
        EditKudosBoxComponent,
        AsyncPipe,
        MatTooltip,
        ReactionsComponent,
        ThreadsComponent,
        KudosRecipientsDisplayComponent,
        LinkPreviewComponent,
        LinkyModule,
        MatMenuModule,
        ReviewBadgeComponent,
        PrivateBadgeComponent,
        ManagerPanelComponent
    ]
})
export class KudosCardComponent {

    @Input({required: true})
    kudos: Kudos;

    @Input({required: true})
    teamId: string;

    @Input() isOdd: boolean;

    @Input() userInfos: UserInfos;

    @Output()
    onEdit: EventEmitter<Kudos> = new EventEmitter();

    @Output()
    onDelete: EventEmitter<Kudos> = new EventEmitter();

    @Output()
    onModerate: EventEmitter<Kudos> = new EventEmitter();

    @ViewChild('threadsComponent')
    private threadsComponent: ThreadsComponent;

    protected showTimelineItemOptions: boolean;

    protected timelineItemEditMode: boolean;

    protected showThreads: boolean = true;

    protected isHandset$ =  this.breakpointObserver.observe([
        Breakpoints.Handset,
    ]).pipe(
        map(result => result.matches),
        distinctUntilChanged()
    )

    constructor(private breakpointObserver: BreakpointObserver) {
    }

    protected kudosBeforeUpdate: Kudos;

    protected editKudos() {
        if (this.kudos.isEditable) {
            this.timelineItemEditMode = true;
            this.kudosBeforeUpdate = structuredClone(this.kudos);
        }
    }

    protected cancelEdit() {
        this.timelineItemEditMode = false;
        this.kudos = structuredClone(this.kudosBeforeUpdate);
    }

    protected deleteKudos() {
        if (this.kudos.isDeletable) {
            this.onDelete.emit(this.kudos);
        }
    }

    protected reportKudos() {
        if (!this.kudos.isDeletable && !this.kudos.isEditable) {
            this.onModerate.emit(this.kudos);
        }
    }

    protected onKudosEdited(updatedKudos: Kudos) {
        if (this.kudos.isEditable) {
            this.onEdit.emit(updatedKudos);
        }
        this.timelineItemEditMode = false;
    }

    protected onThreadsUpdated(timelineItem: TimelineItem & Threads): void {
        if (timelineItem.itemType == "kudosItem"){
            this.kudos = timelineItem as Kudos;
        }
    }

    protected onReacted(timelineItem: {id: string, itemType: TimelineItemType} & Reactions): void {
        if (timelineItem.itemType == "kudosItem"){
            this.kudos = timelineItem as Kudos;
        }
    }

    protected toggleThreads() {
        this.showThreads = !this.showThreads;
    }

    protected discuss() {
        if (this.threadsComponent) {
            this.threadsComponent.discuss = true;
        }
    }

    toGifPreview(linkPreview: LinkPreview): GifPreview {
        return new GifPreview((linkPreview));
    }

    // fix a race condition by using a function
    getThreadsComponent: () => ThreadsComponent = () => this.threadsComponent;
}